import { resolveOffsetFaceChamferMode } from '../../utils/feature/normalizeChamferFeatureDefinition';

/**
 * 圆角/倒角输入槽位解析器。
 *
 * 设计说明：
 * 1. 该类只负责把特征定义投影为“统一输入槽位协议”，不承担 UI 状态与 worker 求解职责；
 * 2. 圆角/倒角宿主、PropertyManager 和后续其它拓扑修改特征都应消费这类槽位定义，而不是各自硬编码选择门禁；
 * 3. 把“选择什么”“以什么模式选”“重复命中如何处理”统一收口后，未来扩展 Draft/Shell/Replace Face 可以沿用同一套主链。
 */
export class BlendFeatureSelectionSlotResolver {
    /**
     * 解析圆角当前激活的输入槽位。
     *
     * @param input 解析输入。
     * @returns 圆角输入槽位定义。
     */
    public resolveFilletSlot(input: {
        definition: FilletFeatureDefinition;
        selectionTarget: 'faces' | 'help-point' | 'partial-edge-reference';
        activeFaceSetId: 'faceSet1' | 'faceSet2' | null;
    }): FeatureSelectionSlotDefinition {
        if (input.selectionTarget === 'partial-edge-reference') {
            return {
                slotId: 'fillet.partial-edge-reference',
                role: 'fillet.partial-edge-reference',
                selectionMode: 'vertex',
                acceptedKinds: ['vertex'],
                cardinality: 'single',
                conflictPolicy: 'replace',
                duplicatePolicy: 'replace',
            };
        }

        if (input.selectionTarget === 'help-point') {
            return {
                slotId: 'fillet.help-point',
                role: 'fillet.help-point',
                selectionMode: 'vertex',
                acceptedKinds: ['vertex'],
                cardinality: 'single',
                conflictPolicy: 'replace',
                duplicatePolicy: 'replace',
            };
        }

        if (input.definition.type === 'face') {
            return {
                slotId: `fillet.face-set.${input.activeFaceSetId ?? 'default'}`,
                role: 'fillet.face-set',
                selectionMode: 'face',
                acceptedKinds: ['face'],
                cardinality: 'multiple',
                conflictPolicy: 'append',
                duplicatePolicy: 'toggle',
            };
        }

        if (input.definition.type === 'variable') {
            return {
                slotId: 'fillet.items.variable',
                role: 'fillet.items',
                selectionMode: 'edge',
                acceptedKinds: ['edge'],
                cardinality: 'multiple',
                conflictPolicy: 'append',
                duplicatePolicy: 'toggle',
            };
        }

        const edgeMode = this.resolveConstantFilletEdgeMode(
            input.definition.edgeFilletMode,
            input.definition.items ?? [],
        );
        return {
            slotId: 'fillet.items.constant',
            role: 'fillet.items',
            selectionMode: edgeMode === 'asymmetric' ? 'edge' : 'face-edge',
            acceptedKinds: ['edge', 'face'],
            cardinality: 'multiple',
            conflictPolicy: 'append',
            duplicatePolicy: 'toggle',
        };
    }

    /**
     * 解析倒角当前激活的输入槽位。
     *
     * @param input 解析输入。
     * @returns 倒角输入槽位定义。
     */
    public resolveChamferSlot(input: {
        definition: ChamferFeatureDefinition;
        selectionTarget: 'faces' | 'help-point' | 'partial-edge-reference';
        activeFaceSetId: 'faceSet1' | 'faceSet2';
    }): FeatureSelectionSlotDefinition {
        if (input.selectionTarget === 'partial-edge-reference') {
            return {
                slotId: 'chamfer.partial-edge-reference',
                role: 'chamfer.partial-edge-reference',
                selectionMode: 'vertex',
                acceptedKinds: ['vertex'],
                cardinality: 'single',
                conflictPolicy: 'replace',
                duplicatePolicy: 'replace',
            };
        }

        if (input.selectionTarget === 'help-point') {
            return {
                slotId: 'chamfer.help-point',
                role: 'chamfer.help-point',
                selectionMode: 'vertex',
                acceptedKinds: ['vertex'],
                cardinality: 'single',
                conflictPolicy: 'replace',
                duplicatePolicy: 'replace',
            };
        }

        if (input.definition.type === 'face-face') {
            return {
                slotId: `chamfer.face-set.${input.activeFaceSetId}`,
                role: 'chamfer.face-set',
                selectionMode: 'face',
                acceptedKinds: ['face'],
                cardinality: 'multiple',
                conflictPolicy: 'append',
                duplicatePolicy: 'toggle',
            };
        }

        if (input.definition.type === 'vertex') {
            return {
                slotId: 'chamfer.items.vertex',
                role: 'chamfer.items',
                selectionMode: 'vertex',
                acceptedKinds: ['vertex'],
                cardinality: 'single',
                conflictPolicy: 'replace',
                duplicatePolicy: 'replace',
            };
        }

        if (
            input.definition.type === 'angle-distance'
            || input.definition.type === 'distance-distance'
        ) {
            return {
                slotId: `chamfer.items.${input.definition.type}`,
                role: 'chamfer.items',
                selectionMode: 'face-edge',
                acceptedKinds: ['edge', 'face'],
                cardinality: 'multiple',
                conflictPolicy: 'append',
                duplicatePolicy: 'toggle',
            };
        }

        if (input.definition.type === 'offset-face') {
            const offsetFaceMode = resolveOffsetFaceChamferMode(input.definition);
            return {
                slotId: 'chamfer.items.offset-face',
                role: 'chamfer.items',
                selectionMode: offsetFaceMode === 'asymmetric' ? 'edge' : 'face-edge',
                acceptedKinds: offsetFaceMode === 'asymmetric' ? ['edge'] : ['edge', 'face'],
                cardinality: 'multiple',
                conflictPolicy: 'append',
                duplicatePolicy: 'toggle',
            };
        }

        return {
            slotId: `chamfer.items.${input.definition.type}`,
            role: 'chamfer.items',
            selectionMode: 'edge',
            acceptedKinds: ['edge'],
            cardinality: 'multiple',
            conflictPolicy: 'append',
            duplicatePolicy: 'toggle',
        };
    }

    /**
     * 判断槽位是否允许吸收指定实体种类。
     *
     * @param slot 槽位定义。
     * @param kind 实体种类。
     * @returns 是否允许吸收。
     */
    public allowsKind(
        slot: FeatureSelectionSlotDefinition,
        kind: 'edge' | 'face' | 'vertex',
    ): boolean {
        return slot.acceptedKinds.includes(kind);
    }

    /**
     * 解析固定大小圆角的有效边模式。
     *
     * @param edgeFilletMode 用户当前选择的边模式。
     * @param items 当前主项目列表。
     * @returns 与求解器对齐后的边模式。
     */
    private resolveConstantFilletEdgeMode(
        edgeFilletMode: FilletFeatureDefinition['edgeFilletMode'],
        items: Array<{ kind: 'edge' | 'face' | 'vertex' }>,
    ): 'symmetric' | 'asymmetric' {
        return items.some((item) => item.kind === 'face')
            ? 'symmetric'
            : (edgeFilletMode ?? 'symmetric');
    }
}
